import { Box, Button, Icon, Typography } from "@outschool/backpack";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { CouponState } from "@outschool/gql-backend-generated";
import { faTag, faTrashAlt } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  FieldErrorMessage,
  TrackedButton
} from "@outschool/ui-components-shared";
import { Input } from "@outschool/ui-legacy-component-library";
import { responsive, useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { useBulkOrderContext, useOrderContext } from "../OrderContext";

const failureStyle = {
  background: "#FAE3E3",
  border: "2px solid #F15D5D !important",
  boxShadow: "inset 0px 1px 2px rgba(102, 113, 123, 0.21) !important"
};

const isValid = (state: CouponState) => state === CouponState.Valid;
const isInvalid = (state: CouponState) =>
  state !== CouponState.Valid && state !== CouponState.NoCoupon;

export default function ApplyPromoCode({
  isOneOnOne
}: {
  isOneOnOne?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\ApplyPromoCode"
  );
  const isMobile = useIsMobile();
  // This local error is used as a hack to enforce client side constraints
  // to require a learner is selected prior to coupon application for 1:1
  const [oneOnOneNoLearnersError, setOneOnOneNoLearnersError] =
    React.useState<string>("");

  const [eventType, setEventType] = React.useState<string>("");

  const orderContext = useOrderContext();
  const bulkOrderContext = useBulkOrderContext();
  const currentOrderContext = bulkOrderContext.learnerItems.length
    ? bulkOrderContext
    : orderContext;
  const {
    pricingBreakdown: { couponState } = {},
    isPricingBreakdownLoading,
    promoCode,
    setPromoCode,
    buyingLearners
  } = currentOrderContext;

  const [showPromoCodeInput, setShowPromoCodeInput] = React.useState<boolean>(
    !!promoCode
  );
  const [localPromoCode, setLocalPromoCode] = React.useState<string>(
    promoCode ?? ""
  );
  const trackEvent = useTrackEvent();

  function reset() {
    setLocalPromoCode("");
    setPromoCode();
    setShowPromoCodeInput(false);
    setEventType("");
  }

  function applyPromoCode(eventTypeName: string) {
    if (localPromoCode === promoCode) {
      return;
    }
    if (buyingLearners.length === 0) {
      setOneOnOneNoLearnersError(t`Please select a learner above.`);
    }

    setEventType(eventTypeName);
    setPromoCode(localPromoCode);
  }

  React.useEffect(() => {
    if (buyingLearners.length > 0) {
      setOneOnOneNoLearnersError("");
    }
  }, [buyingLearners, setOneOnOneNoLearnersError]);

  React.useEffect(() => {
    if (
      !promoCode ||
      !couponState ||
      !eventType ||
      couponState === CouponState.NoCoupon
    ) {
      return;
    }

    trackEvent("enrollment_form_promo_code_applied", {
      couponState,
      eventType,
      promoCode
    });
    setEventType("");
  }, [couponState, eventType, promoCode, trackEvent]);

  const promoCodeUpper = promoCode?.toUpperCase();

  const couponStateIsValid = React.useMemo(
    () =>
      !isPricingBreakdownLoading &&
      !!couponState &&
      isValid(couponState) &&
      !oneOnOneNoLearnersError,
    [couponState, isPricingBreakdownLoading, oneOnOneNoLearnersError]
  );
  const couponStateIsInvalid = React.useMemo(
    () =>
      !isPricingBreakdownLoading &&
      (!couponState || isInvalid(couponState) || !!oneOnOneNoLearnersError),
    [couponState, oneOnOneNoLearnersError, isPricingBreakdownLoading]
  );

  return (
    <Box
      sx={
        isOneOnOne
          ? {
              marginY: "1em"
            }
          : {
              marginY: "1em",
              paddingBottom: "1em",
              borderBottom: "1px solid",
              borderColor: "grey.200"
            }
      }
    >
      <Box
        flex
        sx={{
          alignItems: "left"
        }}
      >
        <TrackedButton
          variant="link"
          data-test-id="coupon-code-toggle"
          trackingName="enrollment_form_promo_code"
          sx={{
            fontSize: "1.4rem",
            fontWeight: "fontWeightBold",
            pointerEvents: showPromoCodeInput ? "none" : "inherit",
            color: showPromoCodeInput ? "grey.700" : "primary.700",

            "&:active, &:focus:not(:disabled)": {
              pointerEvents: showPromoCodeInput ? "none" : "inherit",
              color: showPromoCodeInput ? "grey.700" : "primary.700"
            }
          }}
          onClick={() => {
            setShowPromoCodeInput(true);
          }}
          startIcon={<Icon icon={faTag} />}
        >
          {t("Promo code")}
        </TrackedButton>
      </Box>
      {showPromoCodeInput && (
        <>
          {/* @ts-ignore TS(2345): Argument of type 'CouponState | undefined' is not ... Remove this comment to see the full error message */}
          {!couponStateIsValid ? (
            <>
              <Box
                flex
                sx={{
                  alignItems: "center",
                  marginTop: "0.5em",
                  marginBottom: "0.5em",
                  marginLeft: "1.5em",
                  gap: "0.5em"
                }}
              >
                <Input
                  name={t("Promo code")}
                  data-test-id="coupon-code-input"
                  maxLength={50}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setLocalPromoCode(e.target.value);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key !== "Enter") {
                      return;
                    }

                    applyPromoCode("enter");
                  }}
                  onBlur={() => applyPromoCode("blur")}
                  value={localPromoCode}
                  sx={{
                    width: responsive({ default: "40%", medium: "auto" }),
                    ...(couponStateIsInvalid && localPromoCode === promoCode
                      ? failureStyle
                      : {})
                  }}
                />
                <Button
                  sx={{
                    height: "38px",
                    display: "inline-block"
                  }}
                  data-test-id="coupon-apply-button"
                  size="small"
                  onMouseDown={() => applyPromoCode("click")}
                  disabled={isPricingBreakdownLoading}
                >
                  <Trans t={t}>
                    Apply
                    <Typography
                      variant="inherit"
                      sx={(theme: $TSFixMe) => ({
                        display: "none",

                        [theme.breakpoints.up("md")]: {
                          display: "inline"
                        }
                      })}
                    >
                      {" "}
                      code
                    </Typography>
                  </Trans>
                </Button>
                <Button
                  variant="link"
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: "fontWeightMedium"
                  }}
                  onMouseDown={reset}
                  disabled={isPricingBreakdownLoading}
                >
                  {t("Cancel")}
                </Button>
              </Box>

              {couponStateIsInvalid && localPromoCode === promoCode && (
                <Box
                  flex
                  sx={{
                    alignItems: "center",
                    marginBottom: "1em",
                    marginLeft: "1.5em",

                    "& > div": {
                      fontSize: "0.875em"
                    }
                  }}
                >
                  {!!oneOnOneNoLearnersError ? (
                    <FieldErrorMessage value={oneOnOneNoLearnersError} />
                  ) : (
                    <CouponErrorMessage couponState={couponState} />
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box
              flex
              sx={{
                alignItems: "center",
                marginTop: "0.5em",
                marginBottom: "1em",
                marginLeft: "1.5em"
              }}
            >
              <Box>
                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "0.875em",
                    fontWeight: "medium",
                    color: "success.600"
                  }}
                >
                  {t('Promo "{{promoCode}}" applied.', {
                    promoCode: promoCodeUpper
                  })}
                </Typography>
              </Box>
              <Box
                sx={{
                  marginLeft: "auto"
                }}
              >
                <Button
                  variant="link"
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: "fontWeightMedium"
                  }}
                  onClick={reset}
                >
                  <Icon icon={faTrashAlt} />
                  {!isMobile && (
                    <Typography variant="inherit" sx={{ marginLeft: "0.25em" }}>
                      {t("Remove")}
                    </Typography>
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export function CouponErrorMessage({
  couponState
}: {
  couponState?: CouponState;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\ApplyPromoCode"
  );
  return couponState === CouponState.InvalidUserNotConfirmed ? (
    <FieldErrorMessage
      value={t`To apply this code, confirm your email address.`}
    />
  ) : couponState === CouponState.InvalidNotStarted ? (
    <FieldErrorMessage
      value={t`This promo hasn’t started. Check when your promo starts and try again.`}
    />
  ) : couponState === CouponState.InvalidExpired ||
    couponState === CouponState.InvalidTooManyUsesByUser ||
    couponState === CouponState.InvalidTooManyUsesTotal ||
    couponState === CouponState.InvalidTooManyCouponCodeUses ? (
    <FieldErrorMessage
      value={t`This code has expired. If you have other codes, add them and try again.`}
    />
  ) : couponState === CouponState.InvalidUserNotEligible ||
    couponState === CouponState.InvalidActivityNotEligible ? (
    <FieldErrorMessage
      value={t`Can’t apply this code. If you have other codes, add them and try again.`}
    />
  ) : couponState === CouponState.InvalidActivityFormatNotAllowed ? (
    <FieldErrorMessage
      value={t`This coupon is not valid for this class format.`}
    />
  ) : couponState === CouponState.InvalidMinimumPurchaseNotMet ? (
    <FieldErrorMessage
      value={t`This purchase doesn’t meet the minimum purchase requirement.`}
    />
  ) : couponState === CouponState.InvalidOnlyTeacherCouponsForSubscribers ? (
    <FieldErrorMessage
      value={t`Only teacher offered coupons are supported right now.`}
    />
  ) : (
    <FieldErrorMessage
      value={t`This code doesn’t look right. Check your code and try again.`}
    />
  );
}
